// Assignment templates for one-click pre-filled assignment creation.
// Shape contract:
//   title / prompt / gradeBand map directly onto CreateAssignmentModal state.
//   standards must be real keys from CCSS_ELA_BY_GRADE (see lib/standards.js).
//   rubric.criteria matches the structured shape FlowRubric uses:
//     [{ criterion, levels: [{ score, label, description }] }]

export const ARGUMENTATIVE_TEMPLATE = {
  title: 'The Argument That Matters',
  prompt:
    'Choose an issue you genuinely care about, whether it lives in your school, your community, or the wider world, and take a clear position on it. ' +
    'Build your case with evidence from at least two credible sources, and confront the strongest counterclaim directly instead of pretending it does not exist. ' +
    'Close with a call to action that tells your reader exactly what to do next. ' +
    'Your essay should run 600 to 900 words in a formal, academic tone.',
  gradeBand: '9-10',
  standards: [
    'W.9-10.1a',
    'W.9-10.1b',
    'W.9-10.1c',
    'W.9-10.1d',
    'W.9-10.1e',
  ],
  rubric: {
    title: 'Argumentative Essay Rubric',
    criteria: [
      {
        criterion: 'Claim & Position',
        levels: [
          {
            score: 4,
            label: 'Exceeds',
            description:
              'Introduces a precise, knowledgeable claim, establishes its significance, and clearly distinguishes it from alternate or opposing claims. The position is arguable, focused, and sustained with authority throughout the essay.',
          },
          {
            score: 3,
            label: 'Meets',
            description:
              'States a clear, arguable claim and distinguishes it from opposing views. The position is maintained consistently, though its significance could be established more fully.',
          },
          {
            score: 2,
            label: 'Approaching',
            description:
              'Presents a claim, but it is broad, partially factual rather than arguable, or drifts as the essay develops. Opposing views are acknowledged vaguely or not distinguished from the writer’s position.',
          },
          {
            score: 1,
            label: 'Beginning',
            description:
              'The claim is missing, unclear, or purely a statement of fact or opinion without an arguable position. The reader cannot tell what the writer is arguing for.',
          },
        ],
      },
      {
        criterion: 'Evidence & Support',
        levels: [
          {
            score: 4,
            label: 'Exceeds',
            description:
              'Develops the claim thoroughly with the most relevant evidence from two or more credible sources, and explains how each piece supports the reasoning. Anticipates the audience’s knowledge level and possible biases when selecting and framing evidence.',
          },
          {
            score: 3,
            label: 'Meets',
            description:
              'Supports the claim with relevant, sufficient evidence from at least two credible sources and connects most evidence to the reasoning. Analysis is present but occasionally summarizes rather than explains.',
          },
          {
            score: 2,
            label: 'Approaching',
            description:
              'Uses some evidence, but it is thin, drawn from a single or questionable source, or dropped into the essay without explanation. Reasoning gaps leave the reader to make the connections.',
          },
          {
            score: 1,
            label: 'Beginning',
            description:
              'Relies on unsupported assertions or personal opinion in place of evidence. Sources are absent, uncredited, or not credible.',
          },
        ],
      },
      {
        criterion: 'Counterclaim & Rebuttal',
        levels: [
          {
            score: 4,
            label: 'Exceeds',
            description:
              'Develops the strongest opposing claim fairly and thoroughly, pointing out its genuine strengths before rebutting its limitations with evidence. The rebuttal strengthens the writer’s own argument rather than dismissing the opposition.',
          },
          {
            score: 3,
            label: 'Meets',
            description:
              'Addresses a real counterclaim accurately and responds to it with reasoning or evidence. The rebuttal is logical, though it may engage a weaker version of the opposing view.',
          },
          {
            score: 2,
            label: 'Approaching',
            description:
              'Mentions a counterclaim but misrepresents it, dismisses it in a sentence, or fails to rebut it with reasoning or evidence.',
          },
          {
            score: 1,
            label: 'Beginning',
            description:
              'No counterclaim is acknowledged. The essay argues as if no reasonable person could disagree.',
          },
        ],
      },
      {
        criterion: 'Organization & Conventions',
        levels: [
          {
            score: 4,
            label: 'Exceeds',
            description:
              'Sequences claims, counterclaims, reasons, and evidence logically, using varied transitions and syntax to create cohesion between sections. Maintains a formal style and objective tone, closes with a conclusion that follows from the argument, and shows command of grammar and mechanics with only minor slips.',
          },
          {
            score: 3,
            label: 'Meets',
            description:
              'Organizes the argument in a logical order with functional transitions, a formal tone, and a conclusion that restates the position and call to action. Errors in conventions are present but do not interfere with meaning.',
          },
          {
            score: 2,
            label: 'Approaching',
            description:
              'Shows an attempted structure, but paragraphs are out of sequence, transitions are missing or repetitive, or the tone slides into informal or first-person casual language. Errors in conventions sometimes distract the reader.',
          },
          {
            score: 1,
            label: 'Beginning',
            description:
              'Ideas appear in no discernible order and the essay lacks a conclusion or call to action. Frequent errors in grammar, mechanics, or tone make the argument difficult to follow.',
          },
        ],
      },
    ],
  },
};
